import { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api/client';

const WalletContext = createContext(null);

const STORAGE_KEY = 'flashdead_wallet';

const DEMO_WALLETS = [
    {
        label: 'Owner (Wally)',
        address: '0x6b08236cd4f2be7f49b0201d23e5ba29e06cf800',
    },
    {
        label: 'Heir (Alex)',
        address: '0xa1b2c3d4e5f60718293a4b5c6d7e8f9012345678',
    },
];

export function WalletProvider({ children }) {
    const [wallet, setWallet] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        async function loadProfile() {
            if (!wallet) {
                setProfile(null);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const me = await api.getMe(wallet);
                if (!cancelled) setProfile(me);
            } catch (err) {
                if (!cancelled) {
                    setError(err.message);
                    setProfile(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        loadProfile();
        return () => { cancelled = true; };
    }, [wallet]);

    const connectDemo = (address) => {
        const value = address.toLowerCase();
        localStorage.setItem(STORAGE_KEY, value);
        setWallet(value);
    };

    const connectMetamask = async () => {
        if (!window.ethereum) {
            throw new Error('MetaMask is not installed');
        }
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (!accounts?.[0]) {
            throw new Error('No wallet account returned');
        }
        connectDemo(accounts[0]);
    };

    const disconnect = () => {
        localStorage.removeItem(STORAGE_KEY);
        setWallet('');
        setProfile(null);
    };

    const refreshProfile = async () => {
        if (!wallet) return null;
        const me = await api.getMe(wallet);
        setProfile(me);
        return me;
    };

    const updateProfile = async (body) => {
        if (!wallet) throw new Error('Connect a wallet first');
        const me = await api.updateMe(wallet, body);
        setProfile(me);
        return me;
    };

    const value = {
        wallet,
        profile,
        loading,
        error,
        demoWallets: DEMO_WALLETS,
        isConnected: Boolean(wallet),
        connectDemo,
        connectMetamask,
        disconnect,
        refreshProfile,
        updateProfile,
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
}

export function useWallet() {
    const ctx = useContext(WalletContext);
    if (!ctx) {
        throw new Error('useWallet must be used within WalletProvider');
    }
    return ctx;
}
